import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Download } from 'lucide-react';
import { Button } from '@shared/ui/button';

/**
 * Minimal beforeinstallprompt-based banner. Android Chrome / Edge fire the
 * event when the app is installable; we stash it and surface a small CTA at
 * the bottom of the viewport. iOS Safari does not implement the API, so we
 * fall back to a once-per-device hint after a few visits explaining the
 * "Share → Add to Home Screen" flow.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'trenora.installPrompt.dismissedAt';
const VISITS_KEY = 'trenora.visits';
const IOS_HINT_SHOWN_KEY = 'trenora.installPrompt.iosHintShownAt';

function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS-specific flag
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function dismissedRecently(): boolean {
  const raw = window.localStorage.getItem(DISMISSED_KEY);
  if (!raw) return false;
  const at = Number(raw);
  if (!Number.isFinite(at)) return false;
  // Re-surface after 14 days so a deferred install is not buried forever.
  return Date.now() - at < 14 * 24 * 60 * 60 * 1000;
}

/**
 * Lazy initial state — runs once on mount before render returns, also
 * increments the visits counter so we don't conflate "should the hint show
 * now" with "have we ever seen the user".
 */
function deriveIosVisibleOnce(): boolean {
  if (typeof window === 'undefined') return false;
  if (!isIos()) return false;
  if (isStandalone()) return false;
  if (dismissedRecently()) return false;
  if (window.localStorage.getItem(IOS_HINT_SHOWN_KEY)) return false;
  const raw = window.localStorage.getItem(VISITS_KEY);
  const next = (Number.isFinite(Number(raw)) ? Number(raw) : 0) + 1;
  window.localStorage.setItem(VISITS_KEY, String(next));
  return next >= 3;
}

export function InstallPrompt(): React.JSX.Element | null {
  const { t } = useTranslation();
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null);
  // Compute once — putting this inside an effect that calls setState is
  // flagged by react-hooks/set-state-in-effect under the React 19 rules.
  const [iosVisible, setIosVisible] = useState<boolean>(() => deriveIosVisibleOnce());

  useEffect(() => {
    if (isStandalone()) return;
    if (dismissedRecently()) return;

    function onBeforeInstall(e: Event): void {
      e.preventDefault();
      setEvent(e as BeforeInstallPromptEvent);
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
    };
  }, []);

  function dismiss(): void {
    window.localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setEvent(null);
    if (iosVisible) {
      window.localStorage.setItem(IOS_HINT_SHOWN_KEY, String(Date.now()));
      setIosVisible(false);
    }
  }

  async function install(): Promise<void> {
    if (!event) return;
    await event.prompt();
    const result = await event.userChoice;
    if (result.outcome === 'accepted' || result.outcome === 'dismissed') {
      dismiss();
    }
  }

  if (event) {
    return (
      <div className="border-border bg-card fixed inset-x-3 bottom-20 z-30 flex items-center gap-3 rounded-lg border p-3 shadow-lg">
        <Download className="size-5 shrink-0" aria-hidden="true" />
        <p className="flex-1 text-sm">{t('install.promptAndroid')}</p>
        <Button type="button" size="sm" onClick={() => void install()}>
          {t('install.cta')}
        </Button>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('common.close')}
          className="text-muted-foreground hover:text-foreground shrink-0 rounded p-1"
        >
          <X className="size-4" />
        </button>
      </div>
    );
  }

  if (iosVisible) {
    return (
      <div className="border-border bg-card fixed inset-x-3 bottom-20 z-30 flex items-start gap-3 rounded-lg border p-3 shadow-lg">
        <Download className="size-5 shrink-0" aria-hidden="true" />
        <p className="flex-1 text-sm">{t('install.promptIos')}</p>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('common.close')}
          className="text-muted-foreground hover:text-foreground shrink-0 rounded p-1"
        >
          <X className="size-4" />
        </button>
      </div>
    );
  }

  return null;
}
